// Click tracking — captures click coordinates, target element, and section context.
// Used for heatmap reconstruction. NEVER captures text input or PII.
import { useEffect, useRef, useCallback } from "react";
import { trackEvent } from "@/lib/analytics";

const BATCH_INTERVAL_MS = 5_000;
const MAX_BATCH_SIZE = 20;

interface ClickRecord {
  readonly x: number;
  readonly y: number;
  readonly viewport_width: number;
  readonly viewport_height: number;
  readonly element_tag: string;
  readonly element_id: string;
  readonly section: string;
  readonly timestamp: number;
}

export function useClickTracking(): void {
  const bufferRef = useRef<ClickRecord[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const flush = useCallback(() => {
    if (bufferRef.current.length === 0) return;
    const clicks = [...bufferRef.current];
    bufferRef.current = [];

    trackEvent("click_heatmap_batch", {
      page_url: window.location.pathname,
      clicks,
      count: clicks.length,
    });
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      // Find nearest section or landmark
      const section =
        target.closest("[data-section]")?.getAttribute("data-section") ??
        target.closest("section")?.id ??
        target.closest("main")?.id ??
        "unknown";

      const record: ClickRecord = {
        x: Math.round(e.clientX),
        y: Math.round(e.clientY),
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        element_tag: target.tagName.toLowerCase(),
        element_id: target.id || "",
        section,
        timestamp: Date.now(),
      };

      bufferRef.current = [...bufferRef.current, record];

      if (bufferRef.current.length >= MAX_BATCH_SIZE) {
        flush();
      }
    };

    document.addEventListener("click", onClick, { passive: true });
    timerRef.current = setInterval(flush, BATCH_INTERVAL_MS);

    return () => {
      document.removeEventListener("click", onClick);
      if (timerRef.current) clearInterval(timerRef.current);
      flush();
    };
  }, [flush]);
}

// ── Scroll Heatmap ─────────────────────────────────────────────────────────

export function useScrollHeatmap(): void {
  const maxDepthRef = useRef(0);
  const lastReportRef = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;

      const depth = Math.round((scrollTop / docHeight) * 100);
      if (depth > maxDepthRef.current) {
        maxDepthRef.current = depth;
      }

      // Report every 25% increment
      const bucket = Math.floor(depth / 25) * 25;
      if (bucket > lastReportRef.current && bucket > 0) {
        lastReportRef.current = bucket;
        trackEvent("scroll_heatmap", {
          page_url: window.location.pathname,
          depth_percent: bucket,
          viewport_height: window.innerHeight,
          doc_height: document.documentElement.scrollHeight,
        });
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);

      // Report final max depth on unmount
      if (maxDepthRef.current > 0) {
        trackEvent("scroll_max_depth", {
          page_url: window.location.pathname,
          max_depth_percent: maxDepthRef.current,
        });
      }
    };
  }, []);
}
